/**
 * Shared, security-relevant constants. Kept in one place so the server (the
 * authority) and the web client agree on limits. The SERVER always re-validates;
 * these values let the client fail fast and show friendly copy.
 */

export const LIMITS = {
  /** Max kids that can be invited to a single room (per product spec). */
  MAX_KIDS_PER_ROOM: 8,
  /** Max length of a chat message body. */
  MAX_MESSAGE_LEN: 2000,
  /** Max length of a room name. */
  MAX_ROOM_NAME_LEN: 60,
  /** Screen name bounds for kid profiles. */
  MIN_SCREEN_NAME_LEN: 2,
  MAX_SCREEN_NAME_LEN: 20,
  /** Kid PIN length (numeric). */
  KID_PIN_LEN: 6,
  /** Parent password minimum. */
  MIN_PARENT_PASSWORD_LEN: 12,
  /** Max accepted buddies on a kid's roster. */
  MAX_BUDDIES: 50,
  /** Max outstanding (not-yet-resolved) buddy requests a kid may have open. */
  MAX_PENDING_BUDDY_REQUESTS: 20,
} as const;

/**
 * How a family handles a buddy request that targets one of its kids.
 *  - `both_parents`   — this family must also approve (the safe default).
 *  - `requesting_only`— this family pre-consents; only the requesting kid's
 *                       own parent approves. A family may only ever waive its
 *                       OWN approval, never the other family's.
 * Same-family buddies are always auto-accepted regardless of this setting.
 */
export const BUDDY_APPROVAL_MODES = ['both_parents', 'requesting_only'] as const;
export type BuddyApprovalMode = (typeof BUDDY_APPROVAL_MODES)[number];
export const DEFAULT_BUDDY_APPROVAL_MODE: BuddyApprovalMode = 'both_parents';

/** Lifecycle of a buddy request. */
export const BUDDY_REQUEST_STATUSES = [
  'pending_requester', // waiting on the requesting kid's parent
  'pending_target', // requester's parent approved; waiting on the target kid's parent
  'accepted', // fully approved — a buddyship now exists
  'declined', // a parent declined
  'cancelled', // the requesting kid withdrew
] as const;
export type BuddyRequestStatus = (typeof BUDDY_REQUEST_STATUSES)[number];

/** When a room may open relative to "now". Matches the product flow. */
export const ROOM_OPEN_OFFSETS_MIN = [0, 5, 15, 30] as const;
export type RoomOpenOffsetMin = (typeof ROOM_OPEN_OFFSETS_MIN)[number];

export const ROLES = {
  PARENT: 'parent',
  KID: 'kid',
  ADMIN: 'admin',
} as const;
export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ROOM_STATUS = {
  SCHEDULED: 'scheduled',
  OPEN: 'open',
  CLOSED: 'closed',
} as const;
export type RoomStatus = (typeof ROOM_STATUS)[keyof typeof ROOM_STATUS];

/** Per-room sharing controls. Defaults are the safest (everything off). */
export interface RoomPolicy {
  allowLinks: boolean;
  allowImages: boolean;
  allowEmoji: boolean;
}

export const SAFE_DEFAULT_POLICY: RoomPolicy = {
  allowLinks: false,
  allowImages: false,
  allowEmoji: true,
};

/**
 * Version stamp recorded with each parent's verifiable consent (COPPA). Bump
 * this whenever the Privacy Policy materially changes so the audit trail shows
 * exactly which version a grown-up agreed to. Keep in step with the policy's
 * "Last updated" date in the web LegalPage.
 */
export const PRIVACY_POLICY_VERSION = '2026-06-24';
